import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button as RSButton,
} from "reactstrap";
import { DefaultInput } from "ui-kit-takeat";
import { uploadBannerImage } from "services/bannersService";
import ActionFields from "./ActionFields";
import BannerPreview from "./BannerPreview";
import { SYSTEMS, STATUS_OPTIONS, emptyAction, cleanAction } from "./constants";
import {
  FormLayout,
  FormColumn,
  FieldLabel,
  Row2,
  Select,
  CheckRow,
  SystemsList,
  ErrorText,
  HintText,
  ImageList,
  ImageItem,
  ImageItemBody,
  ImageItemHeader,
  IconBtn,
  AddImageButton,
  Section,
  SectionHeader,
  ButtonRowCard,
  SmallGhostButton,
  RemoveButton,
  PreviewWrap,
} from "./styles";

// Converte ISO/Date pra valor de <input type="datetime-local">.
function toLocalInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

const emptyForm = {
  title: "",
  target_systems: [],
  route_scope: "main",
  route_path: "",
  status: "draft",
  starts_at: "",
  expires_at: "",
  priority: 0,
  // [{ file_id, previewUrl, hasCta, action }]
  images: [],
  buttons: [],
};

function bannerToForm(banner) {
  return {
    title: banner.title || "",
    target_systems: banner.target_systems || [],
    route_scope: banner.route_scope || "main",
    route_path: banner.route_path || "",
    status: banner.status || "draft",
    starts_at: toLocalInput(banner.starts_at),
    expires_at: toLocalInput(banner.expires_at),
    priority: banner.priority || 0,
    images: (banner.images || []).map((im) => ({
      file_id: im.file_id,
      previewUrl: im.file ? im.file.url : null,
      hasCta: !!im.action,
      action: im.action ? { ...emptyAction(), ...im.action } : emptyAction(),
    })),
    buttons: (banner.buttons || []).map((b) => ({
      label: b.label || "",
      variant: b.variant || "primary",
      action: { ...emptyAction(), ...(b.action || {}) },
    })),
  };
}

function BannerFormModal({ isOpen, toggle, banner, onSave, saving }) {
  const [form, setForm] = useState(emptyForm);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(banner ? bannerToForm(banner) : emptyForm);
      setErrors({});
    }
  }, [isOpen, banner]);

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  }, []);

  const toggleSystem = useCallback((key) => {
    setForm((prev) => {
      const has = prev.target_systems.includes(key);
      return {
        ...prev,
        target_systems: has
          ? prev.target_systems.filter((s) => s !== key)
          : [...prev.target_systems, key],
      };
    });
    setErrors((prev) => ({ ...prev, target_systems: null }));
  }, []);

  /* ---------- imagens (carrossel) ---------- */
  const handleAddImage = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = ""; // permite reenviar o mesmo arquivo depois
    setUploadingImage(true);
    try {
      const token = localStorage.getItem("@QRExpress:tokenGd");
      const result = await uploadBannerImage(file, token);
      if (result) {
        setForm((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            {
              file_id: result.id,
              // Preview local (independe do acesso público ao S3).
              previewUrl: URL.createObjectURL(file),
              hasCta: false,
              action: emptyAction(),
            },
          ],
        }));
        setErrors((prev) => ({ ...prev, images: null }));
      }
    } catch (err) {
      console.error("Erro ao subir imagem:", err);
    } finally {
      setUploadingImage(false);
    }
  }, []);

  const updateImage = useCallback((index, patch) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.map((im, i) => (i === index ? { ...im, ...patch } : im)),
    }));
  }, []);

  const removeImage = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }, []);

  const moveImage = useCallback((index, dir) => {
    setForm((prev) => {
      const arr = [...prev.images];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...prev, images: arr };
    });
  }, []);

  /* ---------- botões do footer ---------- */
  const addButton = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      buttons: [
        ...prev.buttons,
        { label: "Novo botão", variant: "primary", action: emptyAction() },
      ],
    }));
  }, []);

  const updateButton = useCallback((index, patch) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  }, []);

  const removeButton = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index),
    }));
  }, []);

  /* ---------- validação ---------- */
  const validate = useCallback(() => {
    const e = {};
    if (!form.title.trim()) e.title = "Título é obrigatório";
    if (form.target_systems.length === 0)
      e.target_systems = "Escolha ao menos um sistema";
    if (form.images.length === 0) e.images = "Adicione ao menos uma imagem";
    if (form.route_scope === "custom" && !form.route_path.trim())
      e.route_path = "Informe a rota";

    form.images.forEach((im, i) => {
      if (im.hasCta) {
        if (im.action.type === "link" && !im.action.url.trim())
          e[`img_${i}`] = "URL do CTA é obrigatória";
        if (im.action.type === "whatsapp" && !im.action.phone.trim())
          e[`img_${i}`] = "Telefone do CTA é obrigatório";
      }
    });

    form.buttons.forEach((b, i) => {
      if (!b.label.trim()) e[`btn_${i}`] = "Texto do botão é obrigatório";
      else if (b.action.type === "link" && !b.action.url.trim())
        e[`btn_${i}`] = "URL do botão é obrigatória";
      else if (b.action.type === "whatsapp" && !b.action.phone.trim())
        e[`btn_${i}`] = "Telefone do botão é obrigatório";
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    const payload = {
      title: form.title.trim(),
      template: "image",
      target_systems: form.target_systems,
      route_scope: form.route_scope,
      route_path: form.route_scope === "custom" ? form.route_path.trim() : null,
      status: form.status,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      priority: Number(form.priority) || 0,
      images: form.images.map((im, i) => ({
        file_id: im.file_id,
        position: i,
        action: im.hasCta ? cleanAction(im.action) : null,
      })),
      buttons: form.buttons.map((b) => ({
        label: b.label.trim(),
        variant: b.variant,
        action: cleanAction(b.action),
      })),
    };
    onSave(payload);
  }, [form, validate, onSave]);

  const isEditing = !!banner;

  // Imagens no formato que o preview espera.
  const previewImages = form.images.map((im) => ({
    url: im.previewUrl,
    action: im.hasCta ? im.action : null,
  }));

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" style={{ maxWidth: 980 }}>
      <ModalHeader toggle={toggle}>
        {isEditing ? "Editar Banner" : "Criar Banner"}
      </ModalHeader>
      <ModalBody>
        <FormLayout>
          {/* ---------- configuração ---------- */}
          <FormColumn>
            <div>
              <DefaultInput
                title="Título (uso interno) *"
                value={form.title}
                onChange={(e) => setField("title", e.target.value)}
              />
              {errors.title && <ErrorText>{errors.title}</ErrorText>}
            </div>

            {/* Imagens (carrossel se houver mais de uma) */}
            <div>
              <FieldLabel>Imagens *</FieldLabel>
              <ImageList>
                {form.images.map((im, i) => (
                  <ImageItem key={i}>
                    {im.previewUrl && <img src={im.previewUrl} alt={`Imagem ${i + 1}`} />}
                    <ImageItemBody>
                      <ImageItemHeader>
                        <span>Imagem {i + 1}</span>
                        <div>
                          <IconBtn
                            type="button"
                            disabled={i === 0}
                            onClick={() => moveImage(i, -1)}
                            title="Subir"
                          >
                            ↑
                          </IconBtn>
                          <IconBtn
                            type="button"
                            disabled={i === form.images.length - 1}
                            onClick={() => moveImage(i, 1)}
                            title="Descer"
                          >
                            ↓
                          </IconBtn>
                          <IconBtn
                            type="button"
                            onClick={() => removeImage(i)}
                            title="Remover"
                          >
                            ✕
                          </IconBtn>
                        </div>
                      </ImageItemHeader>

                      <CheckRow>
                        <input
                          type="checkbox"
                          checked={im.hasCta}
                          onChange={(e) => updateImage(i, { hasCta: e.target.checked })}
                        />
                        Esta imagem tem um CTA (clique dispara uma ação)
                      </CheckRow>
                      {im.hasCta && (
                        <ActionFields
                          label="Ação ao clicar na imagem"
                          action={im.action}
                          onChange={(a) => updateImage(i, { action: a })}
                        />
                      )}
                      {errors[`img_${i}`] && <ErrorText>{errors[`img_${i}`]}</ErrorText>}
                    </ImageItemBody>
                  </ImageItem>
                ))}
              </ImageList>

              <AddImageButton htmlFor="banner-image-upload">
                {uploadingImage ? "Enviando..." : "+ Adicionar imagem"}
                <input
                  id="banner-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleAddImage}
                  disabled={uploadingImage}
                />
              </AddImageButton>
              {errors.images && (
                <div>
                  <ErrorText>{errors.images}</ErrorText>
                </div>
              )}
              <HintText>Mais de uma imagem vira carrossel.</HintText>
            </div>

            <div>
              <FieldLabel>Sistemas onde aparece *</FieldLabel>
              <SystemsList>
                {SYSTEMS.map((s) => (
                  <CheckRow key={s.key}>
                    <input
                      type="checkbox"
                      checked={form.target_systems.includes(s.key)}
                      onChange={() => toggleSystem(s.key)}
                    />
                    {s.label}
                  </CheckRow>
                ))}
              </SystemsList>
              {errors.target_systems && (
                <ErrorText>{errors.target_systems}</ErrorText>
              )}
            </div>

            <div>
              <FieldLabel>Onde aparece</FieldLabel>
              <Row2>
                <Select
                  value={form.route_scope}
                  onChange={(e) => setField("route_scope", e.target.value)}
                >
                  <option value="main">Página principal</option>
                  <option value="custom">Outra rota</option>
                </Select>
                {form.route_scope === "custom" && (
                  <div>
                    <DefaultInput
                      title="Rota"
                      value={form.route_path}
                      onChange={(e) => setField("route_path", e.target.value)}
                      placeholder="/fiscal"
                    />
                    {errors.route_path && <ErrorText>{errors.route_path}</ErrorText>}
                  </div>
                )}
              </Row2>
            </div>

            <Row2>
              <div>
                <FieldLabel>Status</FieldLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setField("status", e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <DefaultInput
                  title="Prioridade"
                  type="number"
                  value={form.priority}
                  onChange={(e) => setField("priority", e.target.value)}
                />
                <HintText>Maior = aparece primeiro</HintText>
              </div>
            </Row2>

            <Row2>
              <div>
                <FieldLabel>Início (opcional)</FieldLabel>
                <Select
                  as="input"
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setField("starts_at", e.target.value)}
                />
              </div>
              <div>
                <FieldLabel>Expiração (opcional)</FieldLabel>
                <Select
                  as="input"
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setField("expires_at", e.target.value)}
                />
              </div>
            </Row2>

            {/* Botões do footer */}
            <Section>
              <SectionHeader>
                <span>Botões do footer</span>
                <SmallGhostButton type="button" onClick={addButton}>
                  + Adicionar botão
                </SmallGhostButton>
              </SectionHeader>

              {form.buttons.length === 0 && (
                <HintText>Sem botões. O modal terá só o "X" pra fechar.</HintText>
              )}

              {form.buttons.map((btn, i) => (
                <ButtonRowCard key={i}>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <DefaultInput
                        title="Texto do botão"
                        value={btn.label}
                        onChange={(e) => updateButton(i, { label: e.target.value })}
                      />
                    </div>
                    <div style={{ width: 150 }}>
                      <FieldLabel>Estilo</FieldLabel>
                      <Select
                        value={btn.variant}
                        onChange={(e) => updateButton(i, { variant: e.target.value })}
                      >
                        <option value="primary">Primário</option>
                        <option value="secondary">Secundário</option>
                      </Select>
                    </div>
                    <div style={{ paddingTop: 26 }}>
                      <RemoveButton type="button" onClick={() => removeButton(i)}>
                        ✕
                      </RemoveButton>
                    </div>
                  </div>
                  <ActionFields
                    action={btn.action}
                    onChange={(a) => updateButton(i, { action: a })}
                  />
                  {errors[`btn_${i}`] && <ErrorText>{errors[`btn_${i}`]}</ErrorText>}
                </ButtonRowCard>
              ))}
            </Section>
          </FormColumn>

          {/* ---------- preview ---------- */}
          <PreviewWrap>
            <FieldLabel>Preview</FieldLabel>
            <BannerPreview
              images={previewImages}
              buttons={form.buttons}
              systems={form.target_systems}
            />
          </PreviewWrap>
        </FormLayout>
      </ModalBody>
      <ModalFooter>
        <RSButton color="secondary" onClick={toggle}>
          Cancelar
        </RSButton>
        <RSButton
          color="primary"
          onClick={handleSubmit}
          disabled={saving || uploadingImage}
        >
          {saving ? "Salvando..." : "Salvar"}
        </RSButton>
      </ModalFooter>
    </Modal>
  );
}

export default BannerFormModal;
